import React, { useState } from 'react';
import { CheckIcon, ChevronDownIcon, LockKeyholeIcon, MapPinIcon, RotateCcwIcon } from 'lucide-react';
import { RecoveryRules, defaultRecoveryRules, recoveryChannels, rulesMatchDefaults } from '../data/recoveryRules';
import { MarketDefinition, marketRegions, placesInRegion } from '../data/marketRegions';
type SubmissionRulesProps = {
  batchName: string;
  brand?: string;
  rules: RecoveryRules;
  // Only admins can lift a negotiated pricing lock.
  canUnlock?: boolean;
  // Preview mode: show the upload's rules without allowing edits.
  readOnly?: boolean;
  // Which rule groups to render. Onboarding shows them one at a time.
  sections?: Array<'channels' | 'pricing' | 'markets'>;
  // Onboarding explains the pricing limit differently than the live settings do.
  pricingHelper?: string;
  onChange: (rules: RecoveryRules) => void;
  onReset: () => void;
};
export function SubmissionRules({
  batchName,
  brand,
  rules,
  canUnlock = false,
  readOnly = false,
  sections = ['channels', 'pricing', 'markets'],
  pricingHelper,
  onChange,
  onReset
}: SubmissionRulesProps) {
  const shows = (section: 'channels' | 'pricing' | 'markets') => sections.includes(section);
  const twoColumn = shows('channels') && (shows('pricing') || shows('markets'));
  const inherited = rulesMatchDefaults(rules, brand, batchName) || readOnly;
  const toggleChannel = (id: string) => onChange({
    ...rules,
    channels: rules.channels.includes(id) ? rules.channels.filter(channel => channel !== id) : [...rules.channels, id]
  });
  const toggleMarket = (market: string) => onChange({
    ...rules,
    markets: rules.markets.includes(market) ? rules.markets.filter(item => item !== market) : [...rules.markets, market]
  });
  return <section className="rounded-2xl border border-[#BB5E40]/20 bg-cream/50 p-5" aria-label={`Recovery rules for ${batchName}`} data-id="element-2752" data-mp="2ilm8n614jwoy">
      {!inherited && <div className="flex justify-end" data-id="element-2753" data-mp="1pxqb5z3qe0r4">
          <button type="button" onClick={onReset} className="inline-flex items-center gap-1.5 rounded-full border border-ink/15 px-3 py-1.5 text-xs font-semibold text-ink/65 transition-colors hover:bg-paper" data-id="element-2754" data-mp="1pwxexs2eetn8">
            <RotateCcwIcon className="h-3 w-3" aria-hidden="true" data-id="element-2755" data-mp="3mq061xln83w" />
            Reset to defaults
          </button>
        </div>}

      <div className={`grid gap-6 ${twoColumn ? 'lg:grid-cols-[1.1fr_0.9fr]' : ''} ${inherited ? '' : 'mt-4'}`} data-id="element-2756" data-mp="7p4m4uwkenze">
        {shows('channels') && <div data-id="element-2757" data-mp="3c0q9n0denxcf">
          <p className="text-xs font-semibold uppercase tracking-wider text-[#94452E]" data-id="element-2758" data-mp="3c29qot6vycf9">Approved channels</p>
          <div className="mt-3 flex flex-wrap gap-2" data-id="element-2759" data-mp="3g221lcjr4uns">
            {recoveryChannels.map(channel => {
            const active = rules.channels.includes(channel.id);
            return <button key={channel.id} type="button" role="switch" aria-checked={active} onClick={() => toggleChannel(channel.id)} disabled={readOnly} className={`inline-flex items-center gap-2 rounded-full border px-3 py-2 text-xs font-semibold transition-colors ${active ? 'border-[#BB5E40] bg-[#BB5E40] text-cream' : 'border-ink/15 bg-paper text-ink/60'} ${readOnly ? 'cursor-default' : active ? '' : 'hover:border-[#BB5E40]/40'}`} data-id="element-2760" data-mp="vbkunyontt9k">
                  {channel.iconUrl && <span aria-hidden="true" className={`h-3.5 w-3.5 shrink-0 ${active ? 'bg-cream' : 'bg-ink/60'}`} style={{
                WebkitMaskImage: `url(${channel.iconUrl})`,
                maskImage: `url(${channel.iconUrl})`,
                WebkitMaskRepeat: 'no-repeat',
                maskRepeat: 'no-repeat',
                WebkitMaskPosition: 'center',
                maskPosition: 'center',
                WebkitMaskSize: 'contain',
                maskSize: 'contain'
              }} data-id="element-2761" data-mp="mqbddyawmcia" />}
                  {channel.name}
                </button>;
          })}
          </div>
          {rules.channels.length === 0 && <p className="mt-3 text-xs font-medium text-[#94452E]" data-id="element-2762" data-mp="2a66plrjzqyjm">Select at least one channel or this upload cannot be activated.</p>}

        </div>}

        {(shows('pricing') || shows('markets')) && <div className="space-y-5" data-id="element-2763" data-mp="217l9s45wlxmj">
          {shows('pricing') && <RuleSlider label="Public pricing limit" detail={pricingHelper ?? 'The maximum discount from MSRP at which this upload can be publicly listed.'} value={rules.indexPrice} min={0} max={90} suffix="% OFF MSRP" locked={readOnly || rules.indexLocked} onLockChange={canUnlock && !readOnly ? indexLocked => onChange({
          ...rules,
          indexLocked
        }) : undefined} onChange={indexPrice => onChange({
          ...rules,
          indexPrice
        })} data-id="element-2764" data-mp="1mi71z0hz7o99" />}
          {shows('markets') && <MarketAccess rules={rules} readOnly={readOnly} onToggleRegion={toggleMarket} onChange={onChange} data-id="element-2765" data-mp="txs59xck764d" />}
        </div>}
      </div>
    </section>;
}

// Region chips with the same country-level drill-down Settings offers, so an
// upload can be limited to specific countries before it is ever analyzed.
function MarketAccess({
  rules,
  readOnly,
  onToggleRegion,
  onChange
}: {
  rules: RecoveryRules;
  readOnly: boolean;
  onToggleRegion: (label: string) => void;
  onChange: (rules: RecoveryRules) => void;
}) {
  const [openRegion, setOpenRegion] = useState<string | null>(null);
  const excluded = rules.excludedPlaces ?? {};
  const setExcluded = (next: Record<string, string[]>) => onChange({
    ...rules,
    excludedPlaces: next
  });
  const togglePlace = (regionId: string, place: string) => {
    const list = excluded[regionId] ?? [];
    setExcluded({
      ...excluded,
      [regionId]: list.includes(place) ? list.filter(item => item !== place) : [...list, place]
    });
  };
  const setAllPlaces = (region: MarketDefinition, enabled: boolean) => setExcluded({
    ...excluded,
    [region.id]: enabled ? [] : placesInRegion(region.id)
  });
  return <div data-id="element-2766" data-mp="1bynw7r3rx3qv">
      <p className="text-xs font-semibold uppercase tracking-wider text-[#94452E]" data-id="element-2767" data-mp="1bx2y5ri932o9">Market access</p>
      <div className="mt-3 flex flex-wrap gap-2" data-id="element-2768" data-mp="1gvnzxvd5huet">
        {marketRegions.map(region => {
        const active = rules.markets.includes(region.label);
        const excludedCount = (excluded[region.id] ?? []).length;
        return <button key={region.id} type="button" role="switch" aria-checked={active} onClick={() => onToggleRegion(region.label)} disabled={readOnly} className={`inline-flex items-center gap-2 rounded-full border px-3 py-2 text-xs font-semibold transition-colors ${active ? 'border-[#BB5E40] bg-[#BB5E40] text-cream' : 'border-ink/15 bg-paper text-ink/60'} ${readOnly ? 'cursor-default' : active ? '' : 'hover:border-[#BB5E40]/40'}`} data-id="element-2769" data-mp="1pvb1qi9sl28e">
              <MapPinIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-2770" data-mp="36fxlovmkadce" />
              {region.label}
              {active && excludedCount > 0 && <span className="rounded-full bg-cream/25 px-1.5 text-[10px] font-semibold" data-id="element-2771" data-mp="1zcvtcrz4jmo">−{excludedCount}</span>}
            </button>;
      })}
      </div>
      <p className="mt-2 text-xs text-ink/50" data-id="element-2772" data-mp="3p4uhx51irfx9">
        {rules.markets.length === 0 ? 'Select at least one market where this inventory can be sold.' : 'Regions where this upload can be marketed and sold. Expand a region to limit it to specific countries.'}
      </p>

      <div className="mt-4 border-t border-ink/10" data-id="element-2773" data-mp="2drry8144ia8v">
        {marketRegions.map(region => {
        const enabled = rules.markets.includes(region.label);
        if (!enabled) return null;
        const excludedList = excluded[region.id] ?? [];
        const total = placesInRegion(region.id).length;
        const open = openRegion === region.id;
        return <div key={region.id} className="border-b border-ink/10 py-3" data-id="element-2774" data-mp="cng2iwe2xcwx">
              <button type="button" onClick={() => setOpenRegion(open ? null : region.id)} aria-expanded={open} aria-controls={`${region.id}-upload-markets`} className="flex w-full items-center justify-between gap-4 text-left" data-id="element-2775" data-mp="10e3d5pfdqgn6">
                <span className="min-w-0" data-id="element-2776" data-mp="vk7f6mz48ts0">
                  <span className="block text-xs font-semibold text-ink" data-id="element-2777" data-mp="1jb4kvi0biuut">{region.label}</span>
                  <span className="mt-0.5 block text-[11px] text-ink/55" data-id="element-2778" data-mp="1o2352sofgd3c">
                    {total - excludedList.length} of {total} markets enabled
                  </span>
                </span>
                <ChevronDownIcon className={`h-4 w-4 shrink-0 text-ink/45 transition-transform duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] ${open ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-2779" data-mp="33tx4kjfidla8" />
              </button>

              {open && <div id={`${region.id}-upload-markets`} className="mt-3 rounded-xl border border-ink/10 bg-paper/70 p-3" data-id="element-2780" data-mp="2ptmdpge786p2">
                  {!readOnly && <div className="flex justify-end gap-2" data-id="element-2781" data-mp="2kwnl52a6o7sz">
                      <button type="button" onClick={() => setAllPlaces(region, true)} className="rounded-full border border-ink/15 px-2.5 py-1 text-[11px] font-semibold text-ink/65 transition-colors hover:bg-cream" data-id="element-2782" data-mp="2yuub7w8w53i3">
                        Select all
                      </button>
                      <button type="button" onClick={() => setAllPlaces(region, false)} className="rounded-full border border-ink/15 px-2.5 py-1 text-[11px] font-semibold text-ink/65 transition-colors hover:bg-cream" data-id="element-2783" data-mp="2b80t5onmi14u">
                        Clear all
                      </button>
                    </div>}
                  <div className="mt-3 space-y-4" data-id="element-2784" data-mp="1si2kb6c04405">
                    {region.groups.map((group, index) => <div key={group.label ?? index} data-id="element-2785" data-mp="1nhy4zjmtxfbi" data-mpi={index}>
                        {group.label && <p className="text-[10px] font-semibold uppercase tracking-wider text-ink/45" data-id="element-2786" data-mp="1ikeglqwf3m5u">{group.label}</p>}
                        <div className={`flex flex-wrap gap-1.5 ${group.label ? 'mt-2' : ''}`} data-id="element-2787" data-mp="3cxns1o8ixhqq">
                          {group.places.map(place => {
                    const off = excludedList.includes(place);
                    return <button key={place} type="button" onClick={() => togglePlace(region.id, place)} aria-pressed={!off} disabled={readOnly} className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors ${off ? 'border-ink/10 bg-paper text-ink/40' : 'border-[#BB5E40]/35 bg-[#BB5E40]/5 text-[#94452E]'} ${readOnly ? 'cursor-default' : off ? 'hover:border-[#BB5E40]/35 hover:text-ink/65' : 'hover:bg-[#BB5E40]/10'}`} data-id="element-2788" data-mp="unruc35l7cfs">
                                {!off && <CheckIcon className="h-2.5 w-2.5" strokeWidth={3} aria-hidden="true" data-id="element-2789" data-mp="213u5mpinv39w" />}
                                {place}
                              </button>;
                  })}
                        </div>
                      </div>)}
                  </div>
                </div>}
            </div>;
      })}
      </div>
    </div>;
}
function RuleSlider({
  label,
  detail,
  value,
  min,
  max,
  suffix,
  locked = false,
  onLockChange,
  onChange
}: {
  label: string;
  detail: string;
  value: number;
  min: number;
  max: number;
  suffix: string;
  locked?: boolean;
  onLockChange?: (locked: boolean) => void;
  onChange: (value: number) => void;
}) {
  const id = `rule-${label.replace(/\s+/g, '-').toLowerCase()}`;
  const lockId = `${id}-lock`;
  return <div data-id="element-2790" data-mp="3jeabavu2flzw">
      <div className="flex items-baseline justify-between gap-3" data-id="element-2791" data-mp="2vqj0jwo6bh0q">
        <label htmlFor={id} className="text-xs font-semibold uppercase tracking-wider text-[#94452E]" data-id="element-2792" data-mp="2vooe2dx97guj">{label}</label>
        <span className={`text-sm font-semibold tabular-nums ${locked ? 'text-ink/55' : 'text-ink'}`} data-id="element-2793" data-mp="2qpbp9nmtnzpb">
          {value}
          <span className="ml-1 text-xs font-medium text-ink/50" data-id="element-2794" data-mp="1azu2nvmn6bom">{suffix}</span>
        </span>
      </div>
      <input id={id} type="range" min={min} max={max} value={value} disabled={locked} onChange={event => onChange(Number(event.target.value))} className={`mt-2 w-full accent-[#BB5E40] ${locked ? 'cursor-not-allowed opacity-60' : ''}`} data-id="element-2795" data-mp="x8yygwn38ird" />
      {onLockChange && <div className="mt-2 flex items-center gap-2" data-id="element-2796" data-mp="1txolf1dko1k7">
          <input id={lockId} type="checkbox" checked={locked} onChange={event => onLockChange(event.target.checked)} className="h-3.5 w-3.5 accent-[#BB5E40]" data-id="element-2797" data-mp="1fzgqt040dcc7" />
          <label htmlFor={lockId} className="flex cursor-pointer items-center gap-1.5 text-xs font-semibold text-ink/65" data-id="element-2798" data-mp="4j06asdlk5ih">
            <LockKeyholeIcon className={`h-3.5 w-3.5 ${locked ? 'text-[#BB5E40]' : 'text-ink/40'}`} aria-hidden="true" data-id="element-2799" data-mp="9kphc80tkj6k" />
            Lock
          </label>
        </div>}
      <p className={`mt-1 text-xs ${locked ? 'font-medium text-[#94452E]' : 'text-ink/50'}`} data-id="element-2800" data-mp="k104ksynj3kx">
        {locked ? `Locked at the agreed ${value}% off MSRP.${onLockChange ? ' Unlock to adjust.' : ' Contact ManyCo to change your agreement.'}` : detail}
      </p>
    </div>;
}
export { defaultRecoveryRules };